import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { storefrontApiRequest, type Money } from "@/lib/shopify";

/**
 * Her Collection cart (Phase One). Uses Shopify's Cart API so lines exist in
 * Shopify, each carrying its own athlete attribution as a line attribute.
 * Checkout is intentionally NOT exposed: checkoutUrl is never stored or opened.
 */
export const CART_CHECKOUT_ENABLED = false;
/**
 * Controlled-test switch: when true, checkout opens ONLY for a signed-in admin viewing Admin Preview
 * (?adminPreview=1) on a preview/test collection. Public checkout stays governed by CART_CHECKOUT_ENABLED.
 * Turn back to false immediately after the controlled test.
 */
export const ADMIN_TEST_CHECKOUT_ENABLED = true;
export const adminTestCheckoutAllowed = (o: { isAdmin: boolean; preview: boolean; adminPreviewParam: boolean }, enabled = ADMIN_TEST_CHECKOUT_ENABLED) =>
  CART_CHECKOUT_ENABLED || (enabled && o.isAdmin && o.preview && o.adminPreviewParam);
export const ATHLETE_LINE_ATTRIBUTE = "_siis_athlete_id";

export type CartLine = {
  lineId: string | null;
  variantId: string;
  productId: string;
  productTitle: string;
  variantTitle: string;
  imageUrl: string | null;
  price: Money;
  quantity: number;
  selectedOptions: { name: string; value: string }[];
  /** Permanent SIIS athlete id, captured per line at add time. Never derived from the cart. */
  athleteId: string;
  athleteName: string;
};

type UserError = { field: string[] | null; message: string };
type Result = { ok: true; lineId?: string } | { ok: false; cartNotFound?: boolean; message: string };

const LINE_FIELDS = `lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } } } }`;
const CREATE = `mutation($input: CartInput!) { cartCreate(input: $input) { cart { id ${LINE_FIELDS} } userErrors { field message } } }`;
const ADD = `mutation($cartId: ID!, $lines: [CartLineInput!]!) { cartLinesAdd(cartId: $cartId, lines: $lines) { cart { id ${LINE_FIELDS} } userErrors { field message } } }`;
const UPDATE = `mutation($cartId: ID!, $lines: [CartLineUpdateInput!]!) { cartLinesUpdate(cartId: $cartId, lines: $lines) { cart { id } userErrors { field message } } }`;
const REMOVE = `mutation($cartId: ID!, $lineIds: [ID!]!) { cartLinesRemove(cartId: $cartId, lineIds: $lineIds) { cart { id } userErrors { field message } } }`;
const QUERY = `query($id: ID!) { cart(id: $id) { id totalQuantity } }`;

const notFound = (errs: UserError[]) =>
  errs.some((e) => /cart not found|does not exist/i.test(e.message));

const lineInput = (l: Pick<CartLine, "variantId" | "quantity" | "athleteId">) => ({
  merchandiseId: l.variantId,
  quantity: l.quantity,
  attributes: [{ key: ATHLETE_LINE_ATTRIBUTE, value: l.athleteId }],
});

const findLine = (cart: any, variantId: string): string | undefined =>
  cart?.lines?.edges?.find((e: any) => e.node.merchandise?.id === variantId)?.node?.id;

/** Integer-cent subtotal (no float drift). */
export const cartSubtotalCents = (lines: CartLine[]) =>
  lines.reduce((s, l) => s + Math.round(Number(l.price.amount) * 100) * l.quantity, 0);

export const linesByAthlete = (lines: CartLine[]) =>
  lines.reduce<Record<string, CartLine[]>>((acc, l) => ((acc[l.athleteId] ??= []).push(l), acc), {});

type State = {
  lines: CartLine[];
  cartId: string | null;
  isLoading: boolean;
  error: string | null;
  addLine: (l: Omit<CartLine, "lineId">) => Promise<boolean>;
  setQuantity: (variantId: string, qty: number) => Promise<void>;
  removeLine: (variantId: string) => Promise<void>;
  clear: () => void;
  sync: () => Promise<void>;
};

const friendly = (e: unknown) => (e instanceof Error ? e.message : "Something went wrong.");

export const useHerCollectionCart = create<State>()(
  persist(
    (set, get) => {
      const run = async (fn: () => Promise<Result>, onOk: (r: { lineId?: string }) => void) => {
        set({ isLoading: true, error: null });
        try {
          const r = await fn();
          if (r.ok) {
            onOk(r);
            return true;
          }
          const f = r as { cartNotFound?: boolean; message: string };
          if (f.cartNotFound) get().clear();
          set({ error: f.message });
          return false;
        } catch (e) {
          set({ error: `Couldn't reach the shop. ${friendly(e)}` });
          return false;
        } finally {
          set({ isLoading: false });
        }
      };

      return {
        lines: [],
        cartId: null,
        isLoading: false,
        error: null,

        addLine: async (item) => {
          const { cartId, lines } = get();
          const existing = lines.find((l) => l.variantId === item.variantId);
          if (existing && existing.athleteId !== item.athleteId) {
            set({ error: "This item is already in your bag for a different athlete." });
            return false;
          }
          if (existing?.lineId && cartId) {
            const qty = existing.quantity + item.quantity;
            return run(
              async () => {
                const d: any = await storefrontApiRequest(UPDATE, { cartId, lines: [{ id: existing.lineId, quantity: qty }] });
                const errs: UserError[] = d?.data?.cartLinesUpdate?.userErrors ?? [];
                return errs.length ? { ok: false, cartNotFound: notFound(errs), message: errs[0].message } : { ok: true };
              },
              () => set({ lines: get().lines.map((l) => (l.variantId === item.variantId ? { ...l, quantity: qty } : l)) }),
            );
          }
          if (!cartId) {
            return run(
              async () => {
                const d: any = await storefrontApiRequest(CREATE, { input: { lines: [lineInput(item)] } });
                const errs: UserError[] = d?.data?.cartCreate?.userErrors ?? [];
                const cart = d?.data?.cartCreate?.cart;
                const lineId = findLine(cart, item.variantId);
                if (errs.length || !cart?.id || !lineId) return { ok: false, message: errs[0]?.message ?? "Couldn't add this item." };
                set({ cartId: cart.id });
                return { ok: true, lineId };
              },
              (r) => set({ lines: [{ ...item, lineId: r.lineId ?? null }] }),
            );
          }
          const added = await run(
            async () => {
              const d: any = await storefrontApiRequest(ADD, { cartId, lines: [lineInput(item)] });
              const errs: UserError[] = d?.data?.cartLinesAdd?.userErrors ?? [];
              if (errs.length) return { ok: false, cartNotFound: notFound(errs), message: errs[0].message };
              const lineId = findLine(d?.data?.cartLinesAdd?.cart, item.variantId);
              return lineId ? { ok: true, lineId } : { ok: false, message: "Couldn't add this item." };
            },
            (r) => set({ lines: [...get().lines, { ...item, lineId: r.lineId ?? null }] }),
          );
          // A saved Shopify cart can go stale (expired, completed or emptied elsewhere). When the local bag
          // is empty there is nothing to lose: drop the stale cart and start a fresh one, once.
          if (!added && get().lines.length === 0 && get().cartId !== null) {
            set({ cartId: null });
            return get().addLine(item);
          }
          if (!added && get().lines.length === 0 && get().cartId === null && cartId !== null) return get().addLine(item);
          return added;
        },

        setQuantity: async (variantId, qty) => {
          if (qty <= 0) return get().removeLine(variantId);
          const { cartId, lines } = get();
          const line = lines.find((l) => l.variantId === variantId);
          if (!cartId || !line?.lineId) return;
          await run(
            async () => {
              const d: any = await storefrontApiRequest(UPDATE, { cartId, lines: [{ id: line.lineId, quantity: qty }] });
              const errs: UserError[] = d?.data?.cartLinesUpdate?.userErrors ?? [];
              return errs.length ? { ok: false, cartNotFound: notFound(errs), message: errs[0].message } : { ok: true };
            },
            () => set({ lines: get().lines.map((l) => (l.variantId === variantId ? { ...l, quantity: qty } : l)) }),
          );
        },

        removeLine: async (variantId) => {
          const { cartId, lines } = get();
          const line = lines.find((l) => l.variantId === variantId);
          if (!cartId || !line?.lineId) return;
          await run(
            async () => {
              const d: any = await storefrontApiRequest(REMOVE, { cartId, lineIds: [line.lineId] });
              const errs: UserError[] = d?.data?.cartLinesRemove?.userErrors ?? [];
              return errs.length ? { ok: false, cartNotFound: notFound(errs), message: errs[0].message } : { ok: true };
            },
            () => {
              const next = get().lines.filter((l) => l.variantId !== variantId);
              next.length ? set({ lines: next }) : get().clear();
            },
          );
        },

        clear: () => set({ lines: [], cartId: null }),

        sync: async () => {
          const { cartId } = get();
          if (!cartId) return;
          try {
            const d: any = await storefrontApiRequest(QUERY, { id: cartId });
            const cart = d?.data?.cart;
            if (!cart || cart.totalQuantity === 0) get().clear();
          } catch {
            /* keep local bag on network failure */
          }
        },
      };
    },
    {
      name: "siis-her-collection-cart",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ lines: s.lines, cartId: s.cartId }),
    },
  ),
);

const CHECKOUT_QUERY = `query($id: ID!) { cart(id: $id) { checkoutUrl totalQuantity } }`;

/**
 * Checkout preparation (inactive). Reads a fresh checkoutUrl from Shopify at the moment
 * of the tap — never stored — and returns it exactly as Shopify gave it. Returns null while
 * CART_CHECKOUT_ENABLED is false, so normal visitors can never reach checkout.
 */
export const prepareCheckoutUrl = async (cartId: string | null, enabled = CART_CHECKOUT_ENABLED): Promise<string | null> => {
  if (!enabled || !cartId) return null;
  const d: any = await storefrontApiRequest(CHECKOUT_QUERY, { id: cartId });
  const url = d?.data?.cart?.checkoutUrl;
  if (!url || !d?.data?.cart?.totalQuantity) return null;
  return String(url);
};
